import type { File as FileResource } from '@terafile/shared'
import { useEffect } from 'react'
import { ModalOverlay } from './ModalOverlay.js'
import { VideoPlayer } from './VideoPlayer.js'

interface VideoPlayerModalProps {
  /** 再生対象。null のとき非表示 */
  file: FileResource | null
  onClose: () => void
}

/**
 * オーナー側の動画プレイヤーをモーダルで表示するラッパ。
 *
 * 旧: ファイル一覧の上部にインライン展開 → ファイル数が多いとスクロール戻しが必要で
 *     再生開始に気づきにくかった (issue #33)
 * 新: 中央モーダル + 暗転背景。ESC / 背景クリックで閉じる。
 *
 * 内部の VideoPlayer はそのまま再利用（再生イベント送信・ウォーターマーク等の挙動は変えない）。
 */
export function VideoPlayerModal({ file, onClose }: VideoPlayerModalProps) {
  // モーダル表示中は ESC で閉じる
  useEffect(() => {
    if (!file) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [file, onClose])

  if (!file) return null

  return (
    <ModalOverlay
      className="bg-black/85"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label={`${file.originalName} の再生`}
    >
      {/* ヘッダ: 背景クリックで閉じるため pointer-events: none。閉じるボタンだけ有効化。
          スマホでは "Player" プレフィクスと "(Esc)" ヒントを隠して
          ファイル名と ✕ ボタンに集中する。 */}
      <div className="fixed top-0 left-0 right-0 px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 text-white pointer-events-none z-[51]">
        <div className="min-w-0 flex-1 flex items-center gap-3">
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Player
          </span>
          <span className="text-sm font-medium truncate">{file.originalName}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="pointer-events-auto text-white/80 hover:text-white text-sm px-3 py-1.5 border border-white/30 rounded-md hover:bg-white/10 transition-colors shrink-0"
        >
          <span className="md:hidden" aria-hidden>
            ✕
          </span>
          <span className="hidden md:inline">閉じる (Esc)</span>
        </button>
      </div>

      {/* 本体: 背景クリックを止める */}
      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <VideoPlayer file={file} />
      </div>
    </ModalOverlay>
  )
}
